import { KeyRound, Save, Shield, UserCircle, Users } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@unistocks/components/button/button';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Heading, Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { PermissionManagementSheet } from '@unistocks/features/admin/components/permission-management/permission-management-sheet';
import { UserRolesSheet } from '@unistocks/features/admin/components/user-management/user-roles-sheet';
import { IdentityUserUpdateDto } from '@unistocks/proxy/axios-client';
import {
  userGetQueryKey,
  useUserGetQuery,
  useUserGetRolesQuery,
  useUserUpdateMutation
} from '@unistocks/proxy/axios-client/UserQuery';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import React, { useState } from 'react';
import { ListItem, Separator, View } from 'tamagui';
import { z } from 'zod';

type UserDetailsProps = {
  id: string;
};

const schemaFactory = () =>
  z.object({
    userName: requiredSchema(z.string(), t('AbpIdentity::UserName')),
    name: z.string().nullish(),
    surname: z.string().nullish(),
    email: requiredSchema(z.email(), t('AbpIdentity::DisplayName:Email')),
    phoneNumber: z.string().nullish(),
    password: z
      .string()
      .nullish()
      .transform(value => value || null)
      .refine(
        value => value === null || value.length >= 6,
        t('::ValidationError:Min', { 0: t('AbpIdentity::Password'), 1: 6 })
      ),
    isActive: z.boolean().default(true),
    lockoutEnabled: z.boolean().default(false),
    concurrencyStamp: z.string().nullish().default(null),
    extraProperties: z.record(z.string(), z.any()).default({})
  });

export const UserDetails: React.FC<UserDetailsProps> = ({ id }) => {
  const schema = schemaFactory();
  const queryClient = useQueryClient();
  const [rolesSheetOpen, setRolesSheetOpen] = useState(false);
  const [permissionsSheetOpen, setPermissionsSheetOpen] = useState(false);

  const hasEditUser = usePermission(UnistocksPermissions.UserManagement);
  const hasPermissionManagement = usePermission(UnistocksPermissions.UserManagementPermissions);
  const hasRoleManagement = usePermission(UnistocksPermissions.UserManagementRoles);

  const { data: user, isSuccess } = useUserGetQuery(id);
  const { data: roles } = useUserGetRolesQuery(id);

  const { mutateAsync: updateUser } = useUserUpdateMutation(id, {
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: userGetQueryKey(id) });
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
    }
  });

  const initialValues: IdentityUserUpdateDto | null = isSuccess
    ? {
        userName: user.userName ?? '',
        name: user.name ?? null,
        surname: user.surname ?? null,
        email: user.email ?? '',
        phoneNumber: user.phoneNumber ?? null,
        password: null,
        isActive: user.isActive,
        lockoutEnabled: user.lockoutEnabled,
        roleNames: roles?.items?.map(r => r.name!).filter(Boolean) ?? null,
        concurrencyStamp: user.concurrencyStamp,
        extraProperties: {}
      }
    : null;

  const handleSubmit = (values: IdentityUserUpdateDto) => updateUser(values);

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={t('AbpIdentity::User:Edit')}
        breadcrumbs={[
          {
            key: 'users',
            href: '/(admin)/user-management',
            name: t('AbpIdentity::Users'),
            rootPathName: 'users',
            Icon: props => <Users {...props} />
          }
        ]}
        queryState={{
          queryKeys: [userGetQueryKey(id)],
          loadingText: t('AbpUi::LoadingWithThreeDot')
        }}
      >
        {isSuccess ? (
          <Column gap={'$4'} mt={'$3'}>
            <Row gap={'$3'} alignItems="center">
              <UserCircle size={'$4'} color={'$unistocks'} />
              <Column>
                <Heading size={'$8'}>{user.userName}</Heading>
                <Paragraph color={'$gray10'}>
                  {user.name} {user.surname}
                </Paragraph>
              </Column>
              <View
                ml={'auto'}
                px={'$2'}
                py={'$1'}
                borderRadius={'$2'}
                bg={user.isActive ? '$green8' : '$red8'}
              >
                <Paragraph color={'$white1'} size={'$3'}>
                  {user.isActive
                    ? t('AbpIdentity::DisplayName:IsActive')
                    : t('AbpIdentity::DisplayName:IsInActive')}
                </Paragraph>
              </View>
            </Row>

            <Page.Content>
              <Form
                schema={schema}
                initialValues={initialValues!}
                disabled={!hasEditUser}
                triggerText={t('AbpUi::Save')}
                triggerIcon={Save}
                onSubmit={handleSubmit}
                customTrigger={hasEditUser ? undefined : () => null}
              >
                <Form.Input
                  name="userName"
                  isRequired
                  label={t('AbpIdentity::UserName')}
                  placeholder={t('AbpIdentity::UserName')}
                />
                <Form.Input
                  name="name"
                  label={t('AbpIdentity::DisplayName:Name')}
                  placeholder={t('AbpIdentity::DisplayName:Name')}
                />
                <Form.Input
                  name="surname"
                  label={t('AbpIdentity::DisplayName:Surname')}
                  placeholder={t('AbpIdentity::DisplayName:Surname')}
                />
                <Form.Input
                  name="email"
                  isRequired
                  label={t('AbpIdentity::DisplayName:Email')}
                  placeholder={t('AbpIdentity::DisplayName:Email')}
                  keyboardType="email-address"
                />
                <Form.Input
                  name="phoneNumber"
                  label={t('AbpIdentity::DisplayName:PhoneNumber')}
                  placeholder={t('AbpIdentity::DisplayName:PhoneNumber')}
                  keyboardType="phone-pad"
                />
                <Form.Input
                  name="password"
                  label={t('AbpIdentity::Password')}
                  placeholder={t('AbpIdentity::Password')}
                  type={'password'}
                />
                <Form.Checkbox name="isActive" label={t('AbpIdentity::DisplayName:IsActive')} />
                <Form.Checkbox
                  name="lockoutEnabled"
                  label={t('AbpIdentity::DisplayName:LockoutEnabled')}
                />
              </Form>
            </Page.Content>

            <Page.Content>
              <Row justifyContent="space-between" alignItems="center">
                <Heading size={'$6'}>{t('AbpIdentity::Roles')}</Heading>
                {hasRoleManagement && (
                  <Button
                    variant={'outlined'}
                    size={'$3'}
                    Icon={KeyRound}
                    onPress={() => setRolesSheetOpen(true)}
                  >
                    {t('AbpIdentity::ManageRoles')}
                  </Button>
                )}
              </Row>

              <Separator />

              {roles?.items?.map(role => (
                <ListItem key={role.id} title={role.name} />
              ))}

              {!roles?.items?.length ? (
                <Paragraph color={'$gray10'}>{t('::NoDataAvailableInDatatable')}</Paragraph>
              ) : null}
            </Page.Content>

            {hasPermissionManagement && (
              <Page.Content>
                <Row justifyContent="space-between" alignItems="center">
                  <Heading size={'$6'}>{t('AbpIdentity::Permissions')}</Heading>
                  <Button
                    variant={'outlined'}
                    size={'$3'}
                    Icon={Shield}
                    onPress={() => setPermissionsSheetOpen(true)}
                  >
                    {t('AbpIdentity::ManagePermissions')}
                  </Button>
                </Row>
              </Page.Content>
            )}
          </Column>
        ) : null}
      </Page.ScrollView>

      <UserRolesSheet userId={id} open={rolesSheetOpen} onOpenChange={setRolesSheetOpen} />

      <PermissionManagementSheet
        providerName="U"
        providerKey={id}
        open={permissionsSheetOpen}
        onOpenChange={setPermissionsSheetOpen}
      />
    </Page>
  );
};

UserDetails.displayName = 'UserDetails';
